import { MESSAGE_KEY_IMPORT_CSS_INLINE, MESSAGE_KEY_IMPORT_CSS_URL, MESSAGE_KEY_ORIGIN, MESSAGE_KEY_SET_HTML_FONT_SIZE, RENDER_MODE_IFRAME, RENDER_MODE_ON_SITE, URL_PARAMETER_RECIPIENT_ORIGIN, URL_PARAMETER_RENDER_MODE } from "../../core/const/Constant.js";
import Message, { subscribeMessage } from "../services/WindowMessage.js";
import Observer from "../services/Observer.js";
import Logger from "../../core/utils/Logger.js";
import Utils from "../utils/Common.util.js";
import { subscribeMessageOnce } from "../../core/services/MessageService.js";

import skeletonLoadingHTML from "../html/skeletonLoading.html";

const _appendInlineCSS = (styleString = '') => {
    if (typeof styleString !== "string" || styleString.length === 0) return;
    const style = document.createElement('style');
    style.innerHTML = styleString;
    document.head.appendChild(style);
}

const _appendCSSUrl = url => {
    const link = document.createElement('link');
    link.href = url;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
}

/**
 * 
 * @param {TaiBaseComponent} component 
 */
const subscribeComponentRendered = component => {
    const unsubscribe = Observer.SubscribeDOMRendered(component.selfRoot, ( /** @type{{component: TaiBaseComponent}} */ { component }) => {
        component.onRendered();
        unsubscribe();
    }, { component });

};

let __origin = Utils.IsIFrame() ? Utils.QueryUrlParameter(URL_PARAMETER_RECIPIENT_ORIGIN) : window.location.origin;

subscribeMessageOnce(MESSAGE_KEY_ORIGIN, data => {
    __origin = data;
});

const unSubscribeHtmlFontSize = (() => {
    const fontSizeStyle = document.createElement("style");
    fontSizeStyle.setAttribute('title', 'Dynamic font size')
    document.head.appendChild(fontSizeStyle);
    return subscribeMessage(MESSAGE_KEY_SET_HTML_FONT_SIZE, (data = '') => {
        if (data.length == 0) return;
        fontSizeStyle.innerHTML = `:root { --tai-size-font-html: ${data}}`
    });
})();


/**@returns{string} */
const elemToSelector = (elem, stopElement) => {
    if (!elem) {
        return "";
    }
    const {
        tagName,
        id,
        className,
        parentNode
    } = elem;

    if (tagName === 'HTML') return 'HTML';

    let str = tagName;

    str += (id !== '') ? `#${id}` : '';

    if (className) {
        const classes = className.split(/\s/);
        for (let i = 0; i < classes.length; i++) {
            str += `.${classes[i]}`;
        }
    }

    let childIndex = 1;

    for (let e = elem; e.previousElementSibling; e = e.previousElementSibling) {
        childIndex += 1;
    }

    str += `:nth-child(${childIndex})`;

    return elem === stopElement ? str : `${elemToSelector(parentNode, stopElement)} > ${str}`;
}


const PRIVATE = Symbol('private');

class TaiBaseComponent {

    uuid = '';

    /** @type {{ selfRoot: HTMLElement | null, containerElement: HTMLElement |null, renderMode: RENDER_MODE_IFRAME | RENDER_MODE_ON_SITE }  }} */
    [PRIVATE] = {
        selfRoot: null,
        containerElement: null,
        renderMode: Utils.IsIFrame() ? Utils.QueryUrlParameter(URL_PARAMETER_RENDER_MODE) : RENDER_MODE_IFRAME
    }

    get origin() {
        return __origin
    };

    /**
     *
     * 
     * @readonly
    * @returns {RENDER_MODE_IFRAME | RENDER_MODE_ON_SITE }
     */
    get renderMode() {
        return this[PRIVATE].renderMode;
    }

    /**
     *
     * @param { HTMLElement | null } elem
     */
    set containerElement(elem) {
        this[PRIVATE].containerElement = elem;
    }

    /**
     *
     * 
     * @readonly
     * @memberof TaiBaseComponent
     * @returns {Element | null}
     */
    get containerElement() {
        return this[PRIVATE].containerElement;
    }

    /**
     *
     *
     * @memberof TaiBaseComponent
     * @returns {Element | null}
     */
    get selfRoot() {
        return this[PRIVATE].selfRoot;
    }

    /**
     *
     * @param {Element | null } elem
     * @memberof TaiBaseComponent
     */
    set selfRoot(elem) {
        this[PRIVATE].selfRoot = elem;
        subscribeComponentRendered(this);
    }


    constructor({ holder = document.body, uuid = '' }) {
        this.uuid = uuid;
        if (typeof holder === 'string') {
            this.containerElement = Utils.querySelector(holder);
        } else {
            this.containerElement = holder;
        }
        setTimeout(async () => {
            this.subcribePostMessage();
            this.onInitialized();
        });
    }

    doFirst() {
    }

    render() {
        this.selfRoot = Utils.HtmlToDom(this.template());
        this.containerElement.insertBefore(this.selfRoot, this.containerElement.children[0]);
    }

    onInitialized() {

    }

    onReady() {

    }

    fetchData() {

    }

    onDataFetched() {

    }

    onRendered() {
        Logger.success("[Rendered]", 'Form')
    }

    addClass(name) {
        if (name.length === 0) return;
        this.selfRoot.classList.add(name)
    }

    removeClass(name) {
        if (Array.isArray(name)) {
            name.forEach(cls => {
                if (cls.length > 0)
                    this.selfRoot.classList.remove(cls);
            })
        } else {
            this.selfRoot.classList.remove(name)
        }

    }

    /**
     * 
     * @returns {string}
     */
    template() {
        return '';
    }

    togglerClass(name) {
        if (this.selfRoot.classList.contains(name)) {
            this.selfRoot.classList.remove(name);
        } else {
            this.selfRoot.classList.add(name);
        }
    }

    subcribePostMessage() {
        subscribeMessage(MESSAGE_KEY_IMPORT_CSS_INLINE, (value) => {
            _appendInlineCSS(value)
        })

        subscribeMessage(MESSAGE_KEY_IMPORT_CSS_URL, (urls) => {
            urls.forEach(url => { _appendCSSUrl(url); })
        })
    }

    /**
     * 
     * @param {string} selector 
     * @returns {HTMLElement}
     */
    querySelector(selector) {
        return this.selfRoot.querySelector(selector);
    }

    setShowSekeletonLoading(isShow = false) {

        if (!this.containerElement) return;
        if (isShow && !this._skeletonLoading) {
            this._skeletonLoading = Utils.HtmlToDom(skeletonLoadingHTML);
            this.containerElement.insertBefore(this._skeletonLoading, this.containerElement.firstChild);
        } else if (!isShow && this._skeletonLoading && this.containerElement.contains(this._skeletonLoading)) {
            this.containerElement.removeChild(this._skeletonLoading);
            this._skeletonLoading = null;
        }
    }

    setPageError(msg = '') {
        this.containerElement.innerHTML = `<h2 style='width:100%; text-align:center'>${msg}</h2>`
    }

    doPostSize(width = this.selfRoot?.offsetWidth, height = this.selfRoot?.offsetHeight) {
        Message.PostResize(width, height);
    }

    /**
     * @description Use to detect the "target" contain the selector
     * @param {HTMLElement | EventTarget | null} target 
     * @param {string} selector 
     * @returns {boolean}
     */
    isElementContain(target, selector) {
        return elemToSelector(target).includes(selector);
    }
}

export default TaiBaseComponent;